import { useState, useCallback, useRef } from 'react'
import { Send, Loader2, Paperclip, Smile } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { EMOJIS } from '@/lib/constants'
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_DISPLAY,
  isAllowedFileType,
  isFileSizeAllowed,
  formatFileSize,
  MAX_FILE_SIZE,
  extractPastedFile
} from '@/lib/fileUpload'
import type { useFileUpload } from '@/hooks/useFileUpload'
import { PendingAttachment } from './PendingAttachment'

type FileUploadHook = ReturnType<typeof useFileUpload>

interface SimpleMessageComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  isSending: boolean
  fileUpload: FileUploadHook
  placeholder?: string
  autoFocus?: boolean
}

export function SimpleMessageComposer({
  value,
  onChange,
  onSubmit,
  isSending,
  fileUpload,
  placeholder = 'Type your message...',
  autoFocus = false
}: SimpleMessageComposerProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { pendingFile, handleFileSelect, handleFileRemove, retryUpload } = fileUpload

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const file = extractPastedFile(e.clipboardData)
    if (!file) return

    e.preventDefault()

    if (!isAllowedFileType(file.type)) {
      toast.error('File type not supported', {
        description: `Allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`
      })
      return
    }

    if (!isFileSizeAllowed(file.size)) {
      toast.error('File too large', {
        description: `Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`
      })
      return
    }

    handleFileSelect(file)
  }, [handleFileSelect])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    e.target.value = ''

    if (!isAllowedFileType(file.type)) {
      toast.error('File type not supported', {
        description: `Allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`
      })
      return
    }

    if (!isFileSizeAllowed(file.size)) {
      toast.error('File too large', {
        description: `Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`
      })
      return
    }

    handleFileSelect(file)
  }, [handleFileSelect])

  const insertEmoji = useCallback((emoji: string) => {
    onChange(value + emoji)
    setShowEmojiPicker(false)
  }, [value, onChange])

  const canSubmit = (value.trim() || pendingFile) && !isSending && pendingFile?.status !== 'error'

  return (
    <div className="border-t border-border bg-card p-4 shrink-0">
      <div className="space-y-2">
        {pendingFile && (
          <PendingAttachment
            pendingFile={pendingFile}
            onRemove={isSending ? undefined : handleFileRemove}
            onRetry={retryUpload}
          />
        )}

        {/* Emoji picker */}
        {showEmojiPicker && (
          <div className="flex flex-wrap gap-1 p-2 bg-muted rounded-lg">
            {EMOJIS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => insertEmoji(emoji)}
                className="size-9 text-xl hover:bg-background rounded cursor-pointer flex items-center justify-center"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={onSubmit} className="flex gap-2 items-end">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileChange}
          />

          {/* Attachment button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
            className="cursor-pointer shrink-0"
            title="Attach file"
          >
            <Paperclip className="size-4" />
          </Button>

          {/* Emoji button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isSending}
            className={cn("cursor-pointer shrink-0", showEmojiPicker && "bg-muted")}
            title="Insert emoji"
          >
            <Smile className="size-4" />
          </Button>

          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (canSubmit) {
                  onSubmit(e)
                }
              }
            }}
            placeholder={placeholder}
            className="flex-1 bg-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none min-h-[40px] max-h-[120px]"
            disabled={isSending}
            rows={1}
            style={{ height: 'auto', overflow: 'hidden' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement
              target.style.height = 'auto'
              target.style.height = `${Math.min(target.scrollHeight, 120)}px`
            }}
            autoFocus={autoFocus}
          />

          <Button
            type="submit"
            disabled={!canSubmit}
            className="cursor-pointer shrink-0"
          >
            {isSending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}
